import { createSelector } from 'reselect';
import { get, isEmpty } from 'lodash';
import moment from 'moment';

import { objectToArray } from 'common/helpers';
import {
  boothStatus,
  boothTypes,
  cartTypes,
  globalSettings,
  sponsorshipType,
  sponsorshipFilters,
} from 'common/constants';
import { SPONSORSHIPS } from 'common/sponsorships';

const selectGlobalDomain = (state) => state.get('global');
const selectBoothsDomain = (state) => state.get('booths');
const selectSponsorshipDomain = (state) => state.get('sponsorships');
const selectBoothCartDomain = (state) => state.get('boothCart');
const selectHotelsDomain = (state) => state.get('hotels');
const selectSessionsDomain = (state) => state.get('sessions');
const selectSelectedSessionDomain = (state) => state.get('sessions');

export const selectAppPending = createSelector(
  selectGlobalDomain,
  (state) => state.getIn(['ui', 'appPending']),
);

export const selectAppTheme = createSelector(
  selectGlobalDomain,
  (state) => state.getIn(['ui', 'appTheme']),
);

export const selectAppLoading = createSelector(
  selectGlobalDomain,
  (state) => state.getIn(['ui', 'loading']),
);

export const selectConference = () => createSelector(
  selectGlobalDomain,
  (state) => state.getIn(['data', 'conference']).toJS(),
);

export const selectTitoEventUrl = () => createSelector(
  selectGlobalDomain,
  (state) => {
    const conference = state.getIn(['data', 'conference']).toJS();
    return `${conference.tito_account}/${conference.tito_event}`;
  }
);

export const selectGlobalSettings = () => createSelector(
  selectGlobalDomain,
  (state) => state.getIn(['data', 'settings']).toJS(),
);

export const selectBooths = createSelector(
  selectBoothsDomain,
  (state) => objectToArray(state.getIn(['data', 'booths']).toJS()),
);

export const selectBoothTypes = () => createSelector(
  selectBoothsDomain,
  (state) => {
    const types = state.getIn(['data', 'types']).toJS();
    const booths = objectToArray(state.getIn(['data', 'booths']).toJS());

    return Object.keys(types)
      .filter((k) => types[k].slug !== boothTypes.DIAMOND)
      .map((k) => {
        const type = types[k];
        const qty = booths.filter((b) => b.booth_type === type.id && b.status === boothStatus.AVAILABLE).length;
        return { ...type, qty };
      })
      .sort((a, b) => {
        const nameA = parseFloat(a.price);
        const nameB = parseFloat(b.price);
        if (nameA > nameB) {
          return -1;
        }
        if (nameA < nameB) {
          return 1;
        }

        return 0;
      });
  }
);

export const selectSelectedBooth = createSelector(
  selectBoothsDomain,
  (state) => {
    const booth = state.getIn(['ui', 'selected']);
    if (booth) return state.getIn(['data', 'booths', booth]).toJS();
    return {};
  },
);

export const selectHoveredBooth = createSelector(
  selectBoothsDomain,
  (state) => {
    const booth = state.getIn(['ui', 'hovered']);
    if (booth) return state.getIn(['data', 'booths', booth]).toJS();
    return {};
  },
);

export const selectIsSponsorCart = () => createSelector(
  selectBoothCartDomain,
  (state) => state.getIn(['ui', 'checkoutType']) === cartTypes.SPONSORSHIP,
);

export const selectCartExpiry = () => createSelector(
  selectGlobalDomain,
  selectBoothCartDomain,
  (globalState, boothCartState) => {
    const cart = boothCartState.getIn(['data', 'cart']).toJS();
    const settings = globalState.getIn(['data', 'settings']).toJS();
    const duration = get(cart, 'type', 'booth') === cartTypes.BOOTH ?
      get(settings, `[${globalSettings.BOOTH_EXPIRY}].value`, '0') : get(settings, `[${globalSettings.SPONSORSHIP_EXPIRY}].value`, '0');

    if (!isEmpty(cart)) {
      const expiry = moment(cart.age).add(parseInt(duration, 0), 'minutes');
      const newDuration = moment(cart.age).diff(moment(cart.created), 'minutes');
      return {
        end: expiry.format('MM-DD-YYYY hh:mm:ss a Z'),
        duration: String(parseInt(duration, 10) + newDuration),
      };
    }
    return {};
  }
);

export const getCartTotal = () => createSelector(
  selectGlobalDomain,
  selectBoothCartDomain,
  (globalState, boothCartState) => {
    const tickets = globalState.getIn(['data', 'titoTickets']).toJS();
    const lineItems = boothCartState.getIn(['data', 'lineItems']).toJS();

    if (isEmpty(lineItems)) return 0;
    return Object.keys(lineItems)
      .map((key) => {
        const item = lineItems[key];
        const ticket = tickets[item.ticket];
        return parseInt(ticket.price, 10);
      })
      .reduce((acc, curr) => acc + curr);
  }
);

export const selectCartPending = () => createSelector(
  selectBoothCartDomain,
  (state) => state.getIn(['ui', 'pending']),
);

export const selectCart = () => createSelector(
  selectBoothCartDomain,
  (state) => state.getIn(['data', 'cart']).toJS(),
);

export const selectLineItems = () => createSelector(
  selectBoothCartDomain,
  (state) => {
    const lineItems = state.getIn(['data', 'lineItems']).toJS();
    return Object.keys(lineItems).map((key) => lineItems[key]);
  },
);

export const selectSponsorshipItems = () => createSelector(
  selectSponsorshipDomain,
  selectGlobalDomain,
  selectBoothCartDomain,
  (sponsorshipState, globalState, cartState) => {
    const items = objectToArray(sponsorshipState.getIn(['data', 'items']).toJS());
    const lineItems = objectToArray(cartState.getIn(['data', 'lineItems']).toJS()).filter((l) => l.item_type !== 'booth');
    const sorting = sponsorshipState.getIn(['ui', 'sorting']);
    const sponsorshipItems = items
      .map((i) => {
        const type = sponsorshipState.getIn(['data', 'types', String(i.type)]).toJS();
        const ticket = globalState.getIn(['data', 'titoTickets', String(i.ticket)]).toJS();
        const display = SPONSORSHIPS[i.slug];
        let selected = false;

        if (lineItems.length > 0) {
          selected = lineItems.find((l) => l.metadata.entity_id === i.id);
        }

        return {
          ...i,
          remaining: selected ? (i.quantity - (i.purchased + i.reserved)) + 1 : i.quantity - (i.purchased + i.reserved),
          isDiamond: type.slug === sponsorshipType.DIAMOND,
          isNetworking: type.slug === sponsorshipType.NETWORKING,
          ticket,
          display,
          selected: !!selected,
          lineItem: selected || {},
        };
      })
      .filter((i) => i.display);

    if (sorting === sponsorshipFilters.PRICE_HIGH) {
      return sponsorshipItems.sort((a, b) => {
        const nameA = parseFloat(a.ticket.price);
        const nameB = parseFloat(b.ticket.price);
        if (nameA < nameB) {
          return 1;
        }
        if (nameA > nameB) {
          return -1;
        }
        return 0;
      });
    } else if (sorting === sponsorshipFilters.PRICE_LOW) {
      return sponsorshipItems.sort((a, b) => {
        const nameA = parseFloat(a.ticket.price);
        const nameB = parseFloat(b.ticket.price);
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    } else if (sorting === sponsorshipFilters.AVAILABLE) {
      const sortedSponsorships = sponsorshipItems.sort((a, b) => {
        const nameA = parseFloat(a.ticket.price);
        const nameB = parseFloat(b.ticket.price);
        if (nameA < nameB) {
          return 1;
        }
        if (nameA > nameB) {
          return -1;
        }
        return 0;
      });
      const available = sortedSponsorships.filter((item) => {
        return item.remaining > 0;
      });
      const purchased = sortedSponsorships.filter((item) => {
        return item.remaining === 0;
      });
      return [...available, ...purchased];
    }
    return sponsorshipItems;
  },
);

export const selectHotels = createSelector(
  selectHotelsDomain,
  (globalState) => {
    return objectToArray(globalState.getIn(['data', 'hotels']).toJS());
  },
);

export const selectSessions = createSelector(
  selectSessionsDomain,
  (globalState) => {
    const sessions = objectToArray(globalState.getIn(['data', 'sessions']).toJS());
    return sessions;
  },
);

export const selectSelectedSession = createSelector(
  selectSelectedSessionDomain,
  (globalState) => {
    const selectedSession = globalState.getIn(['ui', 'selectedSession']).toJS();
    return selectedSession;
  },
);



// WEBPACK FOOTER //
// ./src/common/selectors.js